import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useDeepMemo} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {getFlatTeammates} from '../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {LiveDashboardContentAssignedByTeammateModel} from '../../../../models/api/liveDashboardContentModel';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {resolveTeammatesFromTeammatesAndCircles} from '../../../helpers/teammatesHelpers';
import {useAnalyticsTeammatesFilter} from '../../filters/useAnalyticsTeammatesFilter';
import {ReportSection} from '../../reportElements/reportSection/reportSection';
import {LIVE_DASHBOARD_HELP_CENTER_URL, LIVE_DASHBOARD_MAX_TEAMMATES} from '../constants';
import {LiveDashboardAssignedByTeammateEmptyState} from './liveDashboardAssignedByTeammateEmptyState';
import {LiveDashboardAssignedByTeammateFiltersDropdown} from './liveDashboardAssignedByTeammateFiltersDropdown';
import {LiveDashboardTeammateAssignedByTeammateRows} from './liveDashboardAssignedByTeammateRows';
import {
  LIVE_DASHBOARD_SELECTABLE_STATUSES_COUNT,
  SelectableStatusesEnum,
} from './liveDashboardAssignedByTeammateStatusDropdown';
import {RefinementPill} from './refinementPill';

/*
 * Props.
 */

interface LiveDashboardAssignedByTeammateSectionProps {
  panelId: AnalyticsPanelId;
  assignedByTeammate: LiveDashboardContentAssignedByTeammateModel | undefined;

  className?: string;
}

/*
 * Styles.
 */

const StyledReportSection = styled(ReportSection)`
  /*
    Hijack ReportSection's layout to pass the
    height constraint down via flex-grow.
    Proper height constraints are needed for scrolling to work as desired.
  */
  display: flex;
  flex-direction: column;
  overflow-x: hidden;

  /*
    The sum of the following two right padding values
    must equal ReportSection's original right padding value (15px).
  */
  padding-right: 5px;

  & > * {
    padding-right: 10px;
  }
`;

const StyledScrollableDiv = styled.div`
  flex-grow: 1;
  overflow-y: auto;

  /* Disable overscrolling animations which look weird with a sticky element. */
  overscroll-behavior: none;
`;

const StyledVerticalMarginDiv = styled.div`
  margin: 48px 0;
`;

const StyledFiltersFlexDiv = styled.div`
  /*
    Keep filters at the top when scrolling. 
    You may ask, "Why put this in the scrollable area?".
    Well it's the only way I found to keep the sort toggle aligned
    with the assigned count, in Safari, in every situation.
 
    Once Safari supports scrollbar-gutter, we can do better.
  */
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 2;

  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${ellipsis()}
`;

/*
 * Component.
 */

export const LiveDashboardAssignedByTeammateSection: FC<LiveDashboardAssignedByTeammateSectionProps> = ({
  panelId,
  assignedByTeammate,
  className,
}) => {
  const getPanelId = useMemo(() => () => panelId, [panelId]);
  const {renderDropdown: renderTeammatesDropdown, filterValue} = useAnalyticsTeammatesFilter({
    isLiveDashboard: true,
    getPanelId,
    shouldPlaceSelectedFirst: true,
  });

  const allTeammates = useAppSelector(getFlatTeammates);

  // Using `useDeepMemo` so that it behaves nicely as a `useMemo` dependency below.
  const selectedTeammateIds = useDeepMemo(() => filterValue?.teammate_ids ?? [], [filterValue?.teammate_ids]);
  const selectedCircleIds = useDeepMemo(() => filterValue?.circle_ids ?? [], [filterValue?.circle_ids]);

  // Wrapping `resolveTeammatesFromTeammatesAndCircles` in a memo because it can be quite expensive.
  const resolvedTeammatesCount = useMemo(
    () =>
      // We'll probably want a better UX later on but, for now, clamp the pill value to `LIVE_DASHBOARD_MAX_TEAMMATES`
      // to inform the user that only up to `LIVE_DASHBOARD_MAX_TEAMMATES` teammates will be displayed.
      Math.min(
        resolveTeammatesFromTeammatesAndCircles(selectedTeammateIds, selectedCircleIds, allTeammates).size,
        LIVE_DASHBOARD_MAX_TEAMMATES,
      ),

    // We're explicitly excluding teammates from dependencies or any teammate changes would trigger a recompute.
    // Teammate circle changes are rare enough that we don't care if we need a page refresh or a selection change to refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCircleIds, selectedTeammateIds],
  );

  const [isSortingAscending, setSortAscending] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(
    new Set([
      SelectableStatusesEnum.AVAILABLE,
      SelectableStatusesEnum.OFFLINE_OR_BUSY,
      SelectableStatusesEnum.OUT_OF_OFFICE,
    ]),
  );

  const renderRefinementButton = () => {
    const teammatesLabel = (
      <Plural
        id="live-dashboard-assigned-by-teammate-refinement-label-teammates"
        value={resolvedTeammatesCount}
        one="1 teammate"
        other="# teammates"
      />
    );

    const statusesLabel = (
      <Plural
        id="live-dashboard-assigned-by-teammate-refinement-label-statuses"
        value={selectedStatuses.size}
        one="1 status"
        other="# statuses"
      />
    );

    const refinementButtonLabel =
      // If all statuses are included, do not show the number of selected statuses.
      selectedStatuses.size === LIVE_DASHBOARD_SELECTABLE_STATUSES_COUNT ? (
        teammatesLabel
      ) : (
        <>
          {teammatesLabel}, {statusesLabel}
        </>
      );

    return (
      <RefinementPill isOpen={false} hasSelection={resolvedTeammatesCount > 0}>
        {refinementButtonLabel}
      </RefinementPill>
    );
  };

  const renderFirstRow = () => {
    const sortButtonLabel = isSortingAscending ? (
      <Trans id="live-dashboard-assigned-by-teammate-sort-descending-label">Sort descending</Trans>
    ) : (
      <Trans id="live-dashboard-assigned-by-teammate-sort-ascending-label">Sort ascending</Trans>
    );

    return (
      <StyledFiltersFlexDiv>
        <PopoverCoordinator
          renderButton={renderRefinementButton}
          renderModal={(popoverProps) => (
            <LiveDashboardAssignedByTeammateFiltersDropdown
              popoverProps={popoverProps}
              resolvedTeammatesCount={resolvedTeammatesCount}
              statusSelection={selectedStatuses}
              renderTeammatesDropdown={renderTeammatesDropdown}
              onStatusesSelection={(selection) => {
                setSelectedStatuses(selection);
              }}
            />
          )}
        />
        <IconButton
          iconName="sortSmall"
          onClick={() => setSortAscending(!isSortingAscending)}
          label={sortButtonLabel}
        />
      </StyledFiltersFlexDiv>
    );
  };

  return (
    <StyledReportSection
      isDisabled={false}
      className={className}
      reportSectionId={ReportElementIdsEnum.LIVE_DASHBOARD_ASSIGNED_BY_TEAMMATE}
      title={sectionTooltip.title}
      sectionTooltip={sectionTooltip}
    >
      <StyledScrollableDiv>
        {renderFirstRow()}
        {resolvedTeammatesCount > 0 && (
          <LiveDashboardTeammateAssignedByTeammateRows
            panelId={panelId}
            teammateAssignedCounts={assignedByTeammate?.teammateAssignedCounts}
            isSortingAscending={isSortingAscending}
            selectedStatuses={selectedStatuses}
          />
        )}
        {resolvedTeammatesCount === 0 && (
          <StyledVerticalMarginDiv>
            <LiveDashboardAssignedByTeammateEmptyState renderTeammatesDropdown={renderTeammatesDropdown} />
          </StyledVerticalMarginDiv>
        )}
      </StyledScrollableDiv>
    </StyledReportSection>
  );
};

const sectionTooltip = {
  title: <Trans id="live-dashboard-assigned-by-teammate">Assigned by teammate</Trans>,
  tooltipContent: (
    <span>
      <Trans id="analytics-live-assigned-by-teammate-tooltip-description">
        Track total number of open conversations assigned to teammates across <b>all</b> inboxes (not only
        filtered inboxes), in order to compare workload distribution.
      </Trans>
    </span>
  ),
  learnMoreUrl: `${LIVE_DASHBOARD_HELP_CENTER_URL}#assigned_by_teammate_list`,
};
